import { FunctionComponent, useEffect, useState } from "react";
import { WalletIcon, CheckIcon } from "@form/components/common/icons";
import { useTypedSelector } from "@/stores";
import useAccount from "@/hooks/useAccount";
import { useAccount as userWeb3Account } from "wagmi";

import { trimAddr } from "@form/utils/format";
import { Button } from "@mantine/core";
import TokenBalanceVerify from "@form/components/campaign/TokenBalanceVerify";
import useVerifyCampaign from "@form/hooks/useVerifyCampaign";
import mixpanel from "mixpanel-browser";

import type { HoldingForm } from "@form/components/form/modules/holding";

const WalletVerify: FunctionComponent<{
  holding?: HoldingForm;
  available: boolean;
  needVerify: boolean;
}> = ({ holding, available, needVerify }) => {
  const { session, loginState, loginProcessing } = useTypedSelector((state) => {
    return state.account;
  });
  const { address, isConnected: _isConnected, status } = userWeb3Account();
  const [needTrack, setNeedTrack] = useState(false);
  const [isWalletQualified, setWalletQualified] = useState(undefined);
  const { changeVerifyState, result, form } = useVerifyCampaign(`account`);

  const { login } = useAccount();

  const isConnected = _isConnected && available && status === "connected";

  const signed = isConnected && !!session;

  const showSignError = needVerify && !result.account;

  useEffect(() => {
    changeVerifyState(!!signed);
  }, [signed]);

  useEffect(() => {
    if (needTrack && isConnected && (!holding || !holding.values.amount)) {
      mixpanel.track("connect_wallet", {
        is_wallet_qualified: true,
      });
    } else if (
      needTrack &&
      isConnected &&
      holding &&
      typeof isWalletQualified !== "undefined"
    ) {
      mixpanel.track("connect_wallet", {
        is_wallet_qualified: isWalletQualified,
      });
    }
  }, [needTrack, isConnected, isWalletQualified, holding]);

  return (
    <div className={available ? "" : "relative opacity-40 disable"}>
      <div className="flex items-center">
        <div className="flex-1">
          <div className="flex items-center">
            <WalletIcon className="dark:fill-[#87B5FF]"></WalletIcon>
            <span className="text-body text-dark1 font-bold ml-2 dark:text-[#F9F9F9]">
              Wallet
            </span>
            {showSignError && !address && (
              <span className="text-error text-sm ml-2">This is required</span>
            )}
            {address && isConnected && (
              <span className="text-bodyS text-gray_58 ml-2 dark:text-[#B3D4F8]">
                {trimAddr(address)}
              </span>
            )}
            <div className="flex-1"></div>
            {isConnected && (
              <span className="text-green text-bodyS font-bold dark:text-dark_green">
                Connected
              </span>
            )}
          </div>
          <div className="text-bodyS text-dark1 mt-2 dark:text-[#DCECFA]">
            <div>Connect and sign with your wallet.</div>
            <div>Signing is safe and no transactions will be initiated.</div>
          </div>
        </div>
        {!isConnected && (
          <div className="flex-shrink-0 ml-4">
            <Button
              loading={loginProcessing}
              onClick={() => {
                login();
                setNeedTrack(true);
              }}
              variant="outline"
              radius="xl"
              size="md"
            >
              Connect
            </Button>
          </div>
        )}
      </div>
      <div
        className={`mt-2 py-2 px-4 bg-gray_f9 rounded-lg flex items-center justify-between ${
          isConnected ? "" : "hidden"
        } dark:bg-[#192844] ${
          showSignError ? "bg-error/10 border border-error" : ""
        }`}
      >
        <div className="text-body text-dark1 dark:text-[#F9F9F9]">
          Sign your wallet
        </div>
        <div className="flex items-center">
          {!signed && (
            <div
              onClick={login}
              className="mr-2 text-primary text-body font-bold action dark:text-[#6BB5FF]"
            >
              Sign again
            </div>
          )}
          <CheckIcon
            className={`${
              signed ? "fill-green dark:fill-dark_green text-black" : ""
            }`}
          ></CheckIcon>
        </div>
      </div>
      <div
        className={`text-error text-sm mt-1 ${
          showSignError && address ? "flex" : "hidden"
        }`}
      >
        This is required
      </div>
      {holding && isConnected && holding.values.amount ? (
        <TokenBalanceVerify
          verify={holding}
          setWalletQualified={setWalletQualified}
          needVerify={needVerify}
        ></TokenBalanceVerify>
      ) : null}
    </div>
  );
};

export default WalletVerify;
